

"use client";

import React from "react";
import { motion } from "framer-motion";
import { IProduct } from "../types/types";
import { PALETTE } from "../lib/colorPallete";
import { ProductCardProps } from "../types/types";
import Image from "next/image";
import { useAuth } from "../context/AuthContext";
const PlaceholderImage = () => (
  <div className="absolute inset-0 flex items-center justify-center bg-black/5">
    <svg
      className="w-1/4 h-1/4 text-gray-400 opacity-50"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2 2v12a2 2 0 002 2z"
      />
    </svg>
  </div>
);

const ProductCard: React.FC<ProductCardProps> = ({ product, onCardClick }) => {
  const { _id, modelName, price, subtext, category, inStock, wholeSalePrice } =
    product;
  const { isWholesaleUser } = useAuth();

  const formattedPrice = new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
  }).format(isWholesaleUser ? wholeSalePrice : price);

  const findDisplayImage = (p: IProduct): string | undefined => {
    if (p.commonImageUrls?.length > 0) return p.commonImageUrls[0];

    if (p.colors?.length > 0) {
      for (const color of p.colors) {
        if (color.imageUrls && color.imageUrls.length > 0)
          return color.imageUrls[0];
      }
    }

    if (p.colors?.length > 0) {
      for (const color of p.colors) {
        if (color.variantImages && color.variantImages.length > 0) {
          for (const variant of color.variantImages) {
            if (variant.imageUrls && variant.imageUrls.length > 0)
              return variant.imageUrls[0];
          }
        }
      }
    }

    if (p.customization && p.customization.length > 0) {
      for (const custom of p.customization) {
        for (const option of custom.options) {
          if (
            option.customizationSpecificImageUrls &&
            option.customizationSpecificImageUrls.length > 0
          ) {
            return option.customizationSpecificImageUrls[0];
          }
        }
      }
    }

    return undefined;
  };

  const displayImageUrl = findDisplayImage(product);

  return (
    <motion.div
      layoutId={`card-${_id}`}
      onClick={() => onCardClick(product)}
      className="group w-full h-full flex flex-col rounded-2xl overflow-hidden border cursor-pointer transition-shadow duration-300 hover:shadow-xl"
      style={{
        backgroundColor: PALETTE.cream,
        borderColor: `${PALETTE.midGreen}40`,
      }}
      whileHover={{ y: inStock ? -5 : 0 }}
    >
      { }
      <motion.div
        layoutId={`image-${_id}`}
        className="relative w-full aspect-4/3 overflow-hidden"
      >
        {displayImageUrl ? (
          <Image
            src={displayImageUrl}
            alt={modelName}
            objectFit="contain"
            fill
          />
        ) : (
          <PlaceholderImage />
        )}

        { }
        <div className="absolute top-3 left-3 flex items-center gap-2 px-3 py-1.5 rounded-lg backdrop-blur-md bg-white/20 border border-white/30">
          <Image
            src="/logo-tsp.png"
            alt="Logo"
            width={200}
            height={200}
            className="opacity-90 w-20 h-5"
          />
          {

          }
        </div>

        { }
        <div
          className="absolute top-3 right-3 px-3 py-1 mt-1.5 rounded-full text-[10px] font-semibold uppercase tracking-wider backdrop-blur-md"
          style={{
            backgroundColor: `${PALETTE.darkGreen}B3`,
            color: PALETTE.cream,
          }}
        >
          {category}
        </div>

        { }
        {!inStock && (
          <div className="absolute inset-0 bg-black/30 flex items-center justify-center backdrop-blur-[1px]">
            <div className="bg-red-500 text-white px-4 py-2 rounded-full font-semibold text-sm shadow-lg border border-red-400">
              Out of Stock
            </div>
          </div>
        )}
      </motion.div>

      { }
      <div className="grow flex flex-col p-4 sm:p-5">
        <div className="grow">
          { }
          <motion.h3
            layoutId={`name-${_id}`}
            className={`font-serif text-xl sm:text-2xl font-bold ${!inStock ? "opacity-70" : ""}`}
            style={{ color: PALETTE.darkGreen }}
          >
            {modelName}
          </motion.h3>
          { }
          <motion.p
            layoutId={`subtext-${_id}`}
            className={`mt-1 text-sm sm:text-base leading-snug ${!inStock ? "opacity-60" : ""}`}
            style={{ color: `${PALETTE.darkGreen}B3` }}
          >
            {subtext}
          </motion.p>
        </div>

        { }
        <div
          className="mt-5 pt-4 border-t flex items-center justify-between gap-4"
          style={{ borderColor: `${PALETTE.midGreen}40` }}
        >
          <div className="flex flex-col">
            <motion.p
              layoutId={`price-${_id}`}
              className={`font-serif text-lg sm:text-xl font-semibold ${!inStock ? "opacity-50" : ""}`}
              style={{ color: PALETTE.darkGreen }}
            >
              {formattedPrice}
            </motion.p>
            {!inStock && (
              <span className="text-xs text-red-600 font-medium mt-1">
                Currently Unavailable
              </span>
            )}
          </div>

          <div
            className={`inline-flex items-center justify-center h-10 w-10 rounded-full transition-colors duration-300 ${inStock
              ? "group-hover:bg-amber-400"
              : "opacity-40 cursor-not-allowed"
              }`}
            style={{
              backgroundColor: PALETTE.gold,
              color: PALETTE.darkerGreen,
            }}
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 8l4 4m0 0l-4 4m4-4H3"
              />
            </svg>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ProductCard;
